import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "The two ways to build a zoomable dataviz component with d3.zoom and React",
  "description": "",
  "published": "2017-08-25T00:00:00.000Z",
  "redirect_from": ["/blog/two-ways-build-zoomable-dataviz-component-d3-zoom-react/swizec/7753"],
  "categories": "d3js, Front End, react, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`A question I often get is this: `}<em parentName="p">{`"How do you build a zoomable dataviz component?"`}</em></p>
    <p>{`Well, you use `}<a parentName="p" {...{
        "href": "https://github.com/d3/d3-zoom"
      }}>{`d3.zoom`}</a>{`. That gives you `}<inlineCode parentName="p">{`zoom`}</inlineCode>{` events for pinch-to-zoom and the mousewheel. Detects panning too. Just like your users expect from everything else that zooms.`}</p>
    <p>{`Then what?`}</p>
    <p>{`Then you have a choice to make. Do you want to zoom your whole component like it was an image, or do you want to zoom the space between your datapoints? The first looks pretty, the second gives users a chance to see more detail.`}</p>
    <p>{`In a side-by-side comparison, the two zoom effects look like this 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/08/two-different-zooms.gif",
        "alt": "Two ways to zoom with React and d3.zoom"
      }}></img><img parentName="p" {...{
        "src": "two-zoom.gif",
        "alt": null
      }}></img></p>
    <p>{`Both scatterplots use the same random data. Left side zooms like an image, right side zooms the space between datapoints. It even works on a phone, look.`}</p>
    <blockquote>
      <p parentName="blockquote"><a parentName="p" {...{
          "href": "//imgur.com/DHceJ"
        }}></a></p>
    </blockquote>
    <p>{`So how do you make that?`}</p>
    <p>{`You'll need: - 2 React components - 2 D3 scales - 1 D3 zoom - 1 D3 random number generator - 1 line of HTML - 5 lines of CSS - some event hooks - a sprinkle of state - a few props`}</p>
    <p>{`Here we go 🤘`}</p>
    <p>{`See the Pen `}<a parentName="p" {...{
        "href": "https://codepen.io/swizec/pen/XaBrym/"
      }}>{`Two different zooms with D3 and React`}</a>{` by Swizec Teller (`}<a parentName="p" {...{
        "href": "https://codepen.io/swizec"
      }}>{`@swizec`}</a>{`) on `}<a parentName="p" {...{
        "href": "https://codepen.io"
      }}>{`CodePen`}</a>{`.`}</p>
    <h2 {...{
      "id": "chart--component-talks-to-d3zoom"
    }}>{`<`}{`Chart /> component talks to d3.zoom`}</h2>
    <p>{`Our `}<inlineCode parentName="p">{`<Chart />`}</inlineCode>{` component renders two scatterplots and talks to `}<inlineCode parentName="p">{`d3.zoom`}</inlineCode>{` to zoom them. This way we can use a single zoom behavior for the entire SVG, which makes the scatterplots zoom in unison.`}</p>
    <p>{`I also found it more reliable than attaching `}<inlineCode parentName="p">{`d3.zoom`}</inlineCode>{` to individual `}<inlineCode parentName="p">{`<g>`}</inlineCode>{` elements, but couldn't figure out why. I think it assumes internally that it's working on a whole SVG element.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const random = d3.randomNormal(5, 1);
class Chart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: d3.range(200).map((_) => [random(), random()]),
      zoomTransform: null,
    };
    this.zoom = d3
      .zoom()
      .scaleExtent([-5, 5])
      .translateExtent([
        [-100, -100],
        [props.width + 100, props.height + 100],
      ])
      .extent([
        [-100, -100],
        [props.width + 100, props.height + 100],
      ])
      .on("zoom", this.zoomed.bind(this));
  }
  componentDidMount() {
    d3.select(this.refs.svg).call(this.zoom);
  }
  componentDidUpdate() {
    d3.select(this.refs.svg).call(this.zoom);
  }
  zoomed() {
    this.setState({
      zoomTransform: d3.event.transform,
    });
  }
  render() {
    const { zoomTransform } = this.state,
      { width, height } = this.props;

    return (
      <svg width={width} height={height} ref="svg">
        <scatterplot
          data={this.state.data}
          x={0}
          y={0}
          width={width / 2}
          height={height}
          zoomtransform={zoomTransform}
          zoomtype="scale"
        ></scatterplot>
        <scatterplot
          data={this.state.data}
          x={width / 2}
          y={0}
          width={width / 2}
          height={height}
          zoomtransform={zoomTransform}
          zoomtype="detail"
        ></scatterplot>
      </svg>
    );
  }
}
`}</code></pre>
    <p>{`Our chart component breaks down into 4 parts:`}</p>
    <ol>
      <li parentName="ol">{`We use the `}<inlineCode parentName="li">{`constructor`}</inlineCode>{` to generate random `}<inlineCode parentName="li">{`[x, y]`}</inlineCode>{` coordinate pairs and a `}<inlineCode parentName="li">{`d3.zoom`}</inlineCode>{` behavior. `}<inlineCode parentName="li">{`scaleExtent`}</inlineCode>{` defines min and max scaling factor – from `}<inlineCode parentName="li">{`-5`}</inlineCode>{` to `}<inlineCode parentName="li">{`5`}</inlineCode>{` – and `}<inlineCode parentName="li">{`translateExtent`}</inlineCode>{` and `}<inlineCode parentName="li">{`extent`}</inlineCode>{` define movement boundaries. How much do we allow our chart to move around while zooming? We use `}<inlineCode parentName="li">{`100px`}</inlineCode>{` in every direction.`}</li>
      <li parentName="ol">{`In `}<inlineCode parentName="li">{`componentDidMount`}</inlineCode>{` and `}<inlineCode parentName="li">{`componentDidUpdate`}</inlineCode>{`, we call our zoom behavior on the rendered SVG. This attaches touch, drag, and scroll events to the DOM. D3 normalizes them into a single `}<inlineCode parentName="li">{`zoom`}</inlineCode>{` event for us.`}</li>
      <li parentName="ol">{`The `}<inlineCode parentName="li">{`zoomed`}</inlineCode>{` function is our zoom event callback. We update component state with `}<inlineCode parentName="li">{`d3.event.transform`}</inlineCode>{`, which is where D3 puts the information we need to zoom our chart.`}</li>
      <li parentName="ol">{`Our `}<inlineCode parentName="li">{`render`}</inlineCode>{` method draws two `}<inlineCode parentName="li">{`<Scatterplot />`}</inlineCode>{` components inside an `}<inlineCode parentName="li">{`<svg>`}</inlineCode>{` element and gives them some props.`}</li>
    </ol>
    <h2 {...{
      "id": "scatterplot--component-draws-datapoints-and-zooms-itself"
    }}>{`<`}{`Scatterplot /> component draws datapoints and zooms itself`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`<Scatterplot />`}</inlineCode>{` component follows the full integration approach I outline in `}<a parentName="p" {...{
        "href": "https://www.swizec.com/reactd3js"
      }}>{`React+D3v4`}</a>{`. We have D3 stuff in an `}<inlineCode parentName="p">{`updateD3`}</inlineCode>{` function and we call it when props change to update the internal states of D3 objects.`}</p>
    <p>{`One complication we run into is that we use the same scatterplot component for two different types of zoom. That means some bloat, but it's manageable.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class Scatterplot extends React.Component {
  constructor(props) {
    super(props);
    this.updateD3(props);
  }
  componentWillUpdate(nextProps) {
    this.updateD3(nextProps);
  }
  updateD3(props) {
    const { data, width, height, zoomTransform, zoomType } = props;

    this.xScale = d3.scaleLinear()
                    .domain([0, d3.max(data, ([x, y]) => x)])
                    .range([0, width]),
    this.yScale = d3.scaleLinear()
                    .domain([0, d3.max(data, ([x, y]) => y)])
                    .range([0, height]);

    if (zoomTransform && zoomType === "detail") {
      this.xScale.domain(zoomTransform.rescaleX(this.xScale).domain());
      this.yScale.domain(zoomTransform.rescaleY(this.yScale).domain());
    }
  }
  get transform() {
    const { x, y, zoomTransform, zoomType } = this.props;
    let transform = "";

    if (zoomTransform && zoomType === "scale") {
      transform = \`translate(\${x + zoomTransform.x}, \${y + zoomTransform.y}) scale(\${zoomTransform.k})\`;
    }else{
      transform = \`translate(\${x}, \${y})\`;
    }

    return transform;
  }
  render() {
    const { data } = this.props;

    return (
      <g transform={this.transform} ref="scatterplot">
        {data.map(([x, y]) => <circle cx={this.xScale(x)} cy={this.yScale(y)} r={4}>)}
      </circle></g>
    )
  }
}
`}</code></pre>
    <p>{`Much like the `}<inlineCode parentName="p">{`<Chart />`}</inlineCode>{` component, you can think of `}<inlineCode parentName="p">{`<Scatterplot />`}</inlineCode>{` as having 4 parts:`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`constructor`}</inlineCode>{` and `}<inlineCode parentName="li">{`componentWillUpdate`}</inlineCode>{` call `}<inlineCode parentName="li">{`updateD3`}</inlineCode>{` with fresh props to update internal D3 state`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`updateD3`}</inlineCode>{` sets up two linear scales for us. `}<inlineCode parentName="li">{`xScale`}</inlineCode>{` translates between data values and horizontal coordinates, `}<inlineCode parentName="li">{`yScale`}</inlineCode>{` translates between data values and vertical coordinates`}</li>
      <li parentName="ol">{`The third part is split between the bottom of `}<inlineCode parentName="li">{`updateD3`}</inlineCode>{` and `}<inlineCode parentName="li">{`get transform`}</inlineCode>{`. It handles zooming.`}</li>
    </ol>
    <p>{`Inside `}<inlineCode parentName="p">{`updateD3`}</inlineCode>{` we zoom the space between datapoints by changing our scale's domains. `}<inlineCode parentName="p">{`zoomTransform.rescaleX`}</inlineCode>{` takes a scale and returns a changed scale. We take its domain and update `}<inlineCode parentName="p">{`xScale`}</inlineCode>{`. Same for `}<inlineCode parentName="p">{`yScale`}</inlineCode>{`. This updates both the scatterplot's positioning and spacing between datapoints.`}</p>
    <p>{`This will never make intuitive sense to me, but it works.`}</p>
    <p><inlineCode parentName="p">{`get transform`}</inlineCode>{` also handles zooming. It creates an SVG `}<inlineCode parentName="p">{`transform`}</inlineCode>{` attribute which we use to position and scale a scatterplot. We use `}<inlineCode parentName="p">{`translate()`}</inlineCode>{` to move a chart into position and `}<inlineCode parentName="p">{`scale()`}</inlineCode>{` to make it bigger or smaller depending on the factor `}<inlineCode parentName="p">{`zoomTransform`}</inlineCode>{` gives us.`}</p>
    <p>{`Even if we're not zooming, we still `}<inlineCode parentName="p">{`translate()`}</inlineCode>{` the chart so that we can move it around the page and show two scatterplots side by side.`}</p>
    <ol>
      <li parentName="ol">{`The fourth part is our `}<inlineCode parentName="li">{`render`}</inlineCode>{` method. It creates a grouping element, walks through our data and renders circles.`}</li>
    </ol>
    <p>{`You can play with this example on CodePen.`}</p>
    <p>{`See the Pen `}<a parentName="p" {...{
        "href": "https://codepen.io/swizec/pen/XaBrym/"
      }}>{`Two different zooms with D3 and React`}</a>{` by Swizec Teller (`}<a parentName="p" {...{
        "href": "https://codepen.io/swizec"
      }}>{`@swizec`}</a>{`) on `}<a parentName="p" {...{
        "href": "https://codepen.io"
      }}>{`CodePen`}</a>{`.`}</p>
    <p>{`To learn more about putting React and D3v4 together, `}<a parentName="p" {...{
        "href": "https://www.swizec.com/reactd3js"
      }}>{`check out my new book, React+D3v4`}</a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      